import { useEffect, useState } from 'react';
import { useParams, useNavigate, generatePath } from 'react-router-dom';
import {
  formatBalance,
  ResponseError,
  responseErrorsResolver,
  SidePanel,
  SidePanelType,
} from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import {
  changePanelState,
  clearExportBatchesState,
} from './store/slices/batches';
import TransactionList from './list';
import {
  useLazyGetExportBatchByIdQuery,
  useLazyGetExportBatchFileQuery,
} from './store/services';
import { clearExportTransactionListState } from './store/slices/list';

import ROUTES from '../../routes';

import cn from './styles.module.css';

const BatchRead = (): React.JSX.Element => {
  const { batchId, accountId, bankId } = useParams();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [batchError, setBatchError] = useState('');

  const {
    batches: { panelIsOpen, batch },
    list: { transactions, sum },
    banks: { banks },
    accounts: { accounts },
  } = useAppSelector((state) => state.export);

  const [getBatchById, { isLoading, isFetching }] =
    useLazyGetExportBatchByIdQuery();
  const [
    getExportBatchFile,
    { isLoading: batchDownload, isFetching: batchIsFetching },
  ] = useLazyGetExportBatchFileQuery();

  const handleClose = () => {
    dispatch(clearExportBatchesState());

    setTimeout(() => {
      navigate(
        generatePath(ROUTES.EXPORT_BATCHES, {
          bankId: bankId as string,
          accountId: accountId as string,
        }),
      );
    }, 100);
  };

  const getExportBatchFileAction = async (id: string) => {
    const currentBankName = banks.find(
      (item) => item.id === batch?.pspPartnerId,
    )?.name;
    const currentAccontNumber = accounts.find(
      (item) => item.id === batch?.pspAccountId,
    )?.name;

    const response = (await getExportBatchFile(id)) as {
      error: ResponseError;
      data: any;
    };

    if (response.data && currentBankName && currentAccontNumber) {
      const hiddenElement = document.createElement('a');
      const url = window.URL || window.webkitURL;

      const blob = url.createObjectURL(response.data);
      hiddenElement.href = blob;
      hiddenElement.target = '_blank';
      hiddenElement.download = `${currentBankName}, ${currentAccontNumber}, Batch ${batch?.number}.xlsx`;
      hiddenElement.click();
    } else if (response.error) {
      responseErrorsResolver({
        errors: response.error,
        setError: () => {},
        setGlobalError: setBatchError,
      });
    }
  };

  useEffect(() => {
    dispatch(changePanelState(true));

    return () => {
      dispatch(clearExportBatchesState());
      dispatch(clearExportTransactionListState());
    };
  }, [dispatch]);

  return (
    <>
      <SidePanel
        type={SidePanelType.Transactions}
        closeOnClickOutside
        isOpen={panelIsOpen}
        onClose={handleClose}
        title={`All transactions of ${batch?.number || ''}`}
        sidepanelExpandedKey="export-panel"
        footer={
          transactions?.length && sum ? (
            <div className={cn.exportSidepanelFooter}>
              <div className={cn.exportSidepanelFooterLeft}>
                {`All ${transactions?.length} transactions`}
              </div>
              <div
                className={cn.exportSidepanelFooterRight}
              >{`total: ${formatBalance(sum)}`}</div>
            </div>
          ) : undefined
        }
      >
        <div className={cn.exportSidepanelWrapper}>
          <TransactionList
            isLoading={isLoading || isFetching}
            requestInProgress={batchDownload || batchIsFetching}
            getExportBatchesFile={getExportBatchFileAction}
            batchError={batchError}
            createdBatchId={batchId}
            getBatchById={getBatchById}
          />
        </div>
      </SidePanel>
      <div id="arch-popup" />
    </>
  );
};

export default BatchRead;
